import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from './api-client'
import { accountColorPalette } from './lib/account-colors'
import type { DashboardAccountInfo } from '@ecountabl/lib'
import { mapPlaidAccountToNetWorthEntry } from '@ecountabl/lib'

type InstitutionsEntry = {
  name: string
  item_id: string
  totalBalance: number
  error: boolean
}

export type AccountsSliceStatus = 'idle' | 'loading' | 'success' | 'error'

type AccountsSliceState = {
  accounts: DashboardAccountInfo[]
  institutions: InstitutionsEntry[]
  getAccountsError: string
  status: AccountsSliceStatus
}

const initialState: AccountsSliceState = {
  accounts: [],
  institutions: [],
  getAccountsError: '',
  status: 'idle',
}

type FetchAccountsResponse = {
  accounts: DashboardAccountInfo[]
  institutions: InstitutionsEntry[]
  error: string
}

type RejectValue = string

export const fetchAccounts = createAsyncThunk<
  FetchAccountsResponse,
  void,
  { rejectValue: RejectValue }
>(
  'accounts/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.getAccounts()

      const colorUpdates: Record<string, string> = {}
      const formattedAccounts = []
      const institutionsMap: Record<string, InstitutionsEntry> = {}

      for (const acc of res.accounts) {
        if (acc.error) {
          institutionsMap[acc.item_id] = {
            name: acc.institution,
            item_id: acc.item_id,
            error: true,
            totalBalance: 0
          }

          continue
        }

        // if no color, generate a random one and add to colorUpdates
        if (!acc.color) {
          const randomIndex = Math.floor(Math.random() * accountColorPalette.length)
          const color = accountColorPalette[randomIndex].value
          colorUpdates[acc.account_id] = color
          acc.color = color
        }

        const netWorthEntry = mapPlaidAccountToNetWorthEntry(acc)

        // if institution not in institutionsMap, add it
        if (!institutionsMap[acc.item_id]) {
          institutionsMap[acc.item_id] = {
            name: acc.institution,
            item_id: acc.item_id,
            totalBalance: netWorthEntry.addToTotal ? netWorthEntry.value : 0,
            error: false
          }
        } else {
          institutionsMap[acc.item_id].totalBalance += (
            netWorthEntry.addToTotal ? netWorthEntry.value : 0
          )
        }

        // add to formattedAccounts
        formattedAccounts.push(acc)
      }

      if (Object.keys(colorUpdates).length > 0) {
        try {
          api.updateAccountColors(colorUpdates)
        } catch (e) {
          console.warn('Failed to persist account colors', e)
        }
      }

      const institutions = Object.values(institutionsMap)

      return {
        accounts: formattedAccounts,
        institutions: institutions,
        error: ''
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch accounts')
    }
  }
)

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    clearAccounts: (state) => {
      state.accounts = []
      state.institutions = []
      state.getAccountsError = ''
      state.status = 'idle'
    },
    setAccounts: (state, action) => {
      state.accounts = action.payload
      state.status = 'success'
    },
    setInstitutions: (state, action) => {
      state.institutions = action.payload
      state.status = 'success'
    },
    reorderByItemIds: (state, action) => {
      const itemIds: string[] = action.payload || []
      if (!Array.isArray(itemIds) || itemIds.length === 0) return
      const rank: Record<string, number> = {}
      itemIds.forEach((id, idx) => { rank[id] = idx })
      // sort institutions by provided order; non-specified stay after in original order
      const originalInstIndex: Record<string, number> = {}
      state.institutions.forEach((inst, idx: number) => {
        originalInstIndex[inst.item_id] = idx
      })
      state.institutions.sort((a, b) => {
        const aIn = a && rank[a.item_id] !== undefined
        const bIn = b && rank[b.item_id] !== undefined
        if (aIn && bIn) return rank[a.item_id] - rank[b.item_id]
        if (aIn && !bIn) return -1
        if (!aIn && bIn) return 1
        return (originalInstIndex[a.item_id] ?? 0) - (originalInstIndex[b.item_id] ?? 0)
      })
      // sort accounts by provided order; non-specified stay after in original order
      const originalAccIndex: Record<string, number> = {}
      state.accounts.forEach((acc, idx: number) => {
        originalAccIndex[acc.item_id] = Math.min(originalAccIndex[acc.item_id] ?? idx, idx)
      })
      state.accounts.sort((a, b) => {
        const aIn = a && rank[a.item_id] !== undefined
        const bIn = b && rank[b.item_id] !== undefined
        if (aIn && bIn) return rank[a.item_id] - rank[b.item_id]
        if (aIn && !bIn) return -1
        if (!aIn && bIn) return 1
        return (originalAccIndex[a.item_id] ?? 0) - (originalAccIndex[b.item_id] ?? 0)
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.status = 'loading'
        state.getAccountsError = ''
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload.accounts
        state.institutions = action.payload.institutions
        state.getAccountsError = action.payload.error
        state.status = 'success'
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.getAccountsError = action.payload || 'An unknown error occurred'
        state.status = 'error'
      })
  }
})

export const {
  clearAccounts,
  setAccounts,
  setInstitutions,
  reorderByItemIds
} = accountsSlice.actions
export default accountsSlice.reducer